import {
  AppsListDetail20Regular,
  ArrowDownload16Regular,
  ArrowUpload16Regular,
  ChevronDown12Regular,
  ChevronRight12Regular,
  Color16Regular,
  Copy16Regular,
  Delete16Regular,
  Dismiss20Regular,
  DocumentCopy16Regular,
  Filter20Regular,
  Flow16Regular,
  FolderAdd16Regular,
  FolderArrowRight16Regular,
  FolderArrowUp16Regular,
  Info16Regular,
  Link16Regular,
  MoreHorizontal16Regular,
  PanelRight16Regular,
  People16Regular,
  PeopleTeam16Regular,
  Rename16Regular,
  Share16Regular,
  Star16Regular,
} from '@fluentui/react-icons';
import { createFileRoute } from '@tanstack/react-router';
import type { FilterFn, SortFn } from '@tanstack/react-table';
import { useState } from 'react';
import { Button } from '@web/shared/ui/button';
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@web/shared/ui/context-menu';
import {
  createDataTableColumnHelper,
  DataTable,
  DataTableCommand,
  DataTableFloatingCommandBar,
  DataTableSelectionCount,
  useDataTable,
  type DataTableFeatures,
  type DataTableLabels,
  type DataTableRow,
} from '@web/shared/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@web/shared/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@web/shared/ui/empty';
import { Link } from '@web/shared/ui/link';
import { Page } from '@web/shared/ui/page';
import { Toggle } from '@web/shared/ui/toggle';
import { cn } from '@web/shared/util/cn';
import { APP, AppGlyph, FileIcon, FolderIcon, type App } from '../-theme/file-icons';

/**
 * A lista completa da vitrine, montada com o `DataTable` do design system. É o exemplo de referência de lista: ordenação pelo menu da coluna, filtros por
 * tipo, colunas que se escondem, seleção (clique, Ctrl, Shift, cabeçalho), ações da linha
 * no hover, menu de contexto no botão direito, barra de comandos com a seleção, e o painel
 * "Detalhes" ao lado.
 *
 * Medidas:
 *   título "Meus arquivos ⌄"  botão de 36px, raio 8, 24/32 semibold, chevron de 16 (12 dentro)
 *   chips                     42x36 em pílula, 4px de vão; à direita, o seletor de exibição (36x36,
 *                             raio 12, traço `--stroke-card`) e "Detalhes" (105x36)
 *   moldura                   y=185, 1518 de largura; cabeçalho e linhas de 49px
 *   colunas                   seleção 56 · ícone 40 · nome 418 · modificado 200 · por 260 ·
 *                             tamanho 110 · compartilhamento 110 · atividade (o resto)
 *   barra de comandos         52px, avança 8px além da moldura de cada lado
 *   painel "Detalhes"         342 de largura, raio 16, traço `--stroke-card`; cabeçalho 48;
 *                             corpo `--surface-subtle` com seções brancas de raio 4 e 16 de padding
 */
export const Route = createFileRoute('/theme/list')({
  component: MyFilesPage,
});

/* ------------------------------------------------------------------ dados fictícios */

interface Item {
  id: string;
  name: string;
  kind: 'folder' | 'file';
  app?: App;
  shared: boolean;
  modifiedAt: Date;
  /** A data: relativa quando é recente, completa quando é de outro ano. */
  modified: string;
  modifiedBy: string;
  createdAt: Date;
  /** Bytes do arquivo; nas pastas, a contagem de itens. */
  bytes: number;
  items: number;
}

const OWNER = 'Ana Souza';

const ITEMS: Item[] = [
  {
    id: 'copilot',
    name: 'Anexos de conversas',
    kind: 'folder',
    shared: false,
    modifiedAt: new Date('2024-12-03T09:12:00'),
    modified: '3 de dezembro de 2024',
    modifiedBy: OWNER,
    createdAt: new Date('2024-12-03T09:12:00'),
    bytes: 0,
    items: 0,
  },
  {
    id: 'attachments',
    name: 'Attachments',
    kind: 'folder',
    shared: false,
    modifiedAt: new Date('2025-05-26T14:30:00'),
    modified: '26 de maio de 2025',
    modifiedBy: OWNER,
    createdAt: new Date('2025-05-26T14:30:00'),
    bytes: 0,
    items: 0,
  },
  {
    id: 'gravacoes',
    name: 'Gravações',
    kind: 'folder',
    shared: true,
    modifiedAt: new Date('2026-02-10T15:46:00'),
    modified: '10 de fevereiro',
    modifiedBy: OWNER,
    createdAt: new Date('2026-02-10T10:46:00'),
    bytes: 152,
    items: 0,
  },
  {
    id: 'reunioes',
    name: 'Reuniões',
    kind: 'folder',
    shared: true,
    modifiedAt: new Date('2026-02-10T15:46:00'),
    modified: '10 de fevereiro',
    modifiedBy: OWNER,
    createdAt: new Date('2026-02-10T10:46:00'),
    bytes: 152,
    items: 0,
  },
  {
    id: 'whiteboards',
    name: 'Quadros',
    kind: 'folder',
    shared: false,
    modifiedAt: new Date('2026-09-08T18:02:00'),
    modified: 'Ontem',
    modifiedBy: OWNER,
    createdAt: new Date('2026-09-08T18:02:00'),
    bytes: 4096,
    items: 1,
  },
  {
    id: 'documento',
    name: 'Documento.docx',
    kind: 'file',
    app: 'word',
    shared: false,
    modifiedAt: new Date('2026-03-27T11:20:00'),
    modified: '27 de março',
    modifiedBy: OWNER,
    createdAt: new Date('2026-03-27T11:20:00'),
    bytes: 18_739,
    items: 0,
  },
  {
    id: 'documento1',
    name: 'Documento1.docx',
    kind: 'file',
    app: 'word',
    shared: false,
    modifiedAt: new Date('2026-03-27T11:41:00'),
    modified: '27 de março',
    modifiedBy: OWNER,
    createdAt: new Date('2026-03-27T11:41:00'),
    bytes: 63_078,
    items: 0,
  },
  {
    id: 'custos',
    name: 'Planilha de custos.xlsx',
    kind: 'file',
    app: 'excel',
    shared: true,
    modifiedAt: new Date('2026-06-12T16:05:00'),
    modified: '12 de junho',
    modifiedBy: 'Carlos Lima',
    createdAt: new Date('2026-06-01T09:00:00'),
    bytes: 45_158,
    items: 0,
  },
  {
    id: 'apresentacao',
    name: 'Apresentação.pptx',
    kind: 'file',
    app: 'powerpoint',
    shared: false,
    modifiedAt: new Date('2026-08-03T13:15:00'),
    modified: '3 de agosto',
    modifiedBy: OWNER,
    createdAt: new Date('2026-08-03T13:15:00'),
    bytes: 2_201_600,
    items: 0,
  },
  {
    id: 'contrato',
    name: 'Contrato.pdf',
    kind: 'file',
    app: 'pdf',
    shared: false,
    modifiedAt: new Date('2026-08-19T08:40:00'),
    modified: '19 de agosto',
    modifiedBy: OWNER,
    createdAt: new Date('2026-08-19T08:40:00'),
    bytes: 317_440,
    items: 0,
  },
];

const FILTER_APPS: App[] = ['word', 'excel', 'powerpoint', 'pdf'];

/* ------------------------------------------------------------------ formatação */

const SIZE_UNITS = ['bytes', 'KB', 'MB', 'GB'] as const;

/** "18,3 KB": uma casa decimal, vírgula, unidade binária. */
function formatBytes(bytes: number): string {
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const number = unit === 0 ? String(value) : value.toFixed(1).replace('.', ',');
  return `${number} ${SIZE_UNITS[unit]}`;
}

const dateTime = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

function itemType(item: Item): string {
  if (item.kind === 'folder') return 'Pasta';
  return item.app === 'pdf' ? 'Documento PDF' : `Documento do ${APP[item.app ?? 'word'].label}`;
}

/* ------------------------------------------------------------------ colunas */

const helper = createDataTableColumnHelper<Item>();

const kindRank = (row: DataTableRow<Item>) => (row.original.kind === 'folder' ? 0 : 1);

/** Pastas primeiro, depois arquivos; dentro de cada grupo, pelo nome. */
const foldersFirst: SortFn<DataTableFeatures, Item> = (a, b) =>
  kindRank(a) - kindRank(b) || a.original.name.localeCompare(b.original.name, 'pt-BR');

/** Os chips de tipo: com algum ligado, só os arquivos daqueles apps ficam. */
const byApp: FilterFn<DataTableFeatures, Item> = (row, _columnId, filterValue: App[]) =>
  filterValue.length === 0 ||
  (row.original.app !== undefined && filterValue.includes(row.original.app));

const columns = helper.columns([
  helper.display({
    id: 'icon',
    enableHiding: false,
    meta: { width: 40, cellClassName: 'pr-0 pl-[14px]' },
    cell: ({ row }) =>
      row.original.kind === 'folder' ? (
        <FolderIcon shared={row.original.shared} />
      ) : (
        <FileIcon app={row.original.app ?? 'word'} />
      ),
  }),
  helper.accessor('name', {
    header: 'Nome',
    enableHiding: false,
    sortFn: foldersFirst,
    filterFn: byApp,
    meta: { primary: true, width: 418 },
    cell: ({ row, getValue }) => (
      <Link href={`#${row.id}`} variant="subtle" className="max-w-full truncate">
        {getValue()}
      </Link>
    ),
  }),
  helper.accessor('modifiedAt', {
    header: 'Modificado',
    sortFn: 'datetime',
    meta: { width: 200 },
    cell: ({ row }) => row.original.modified,
  }),
  helper.accessor('modifiedBy', {
    header: 'Modificado por',
    sortFn: 'alphanumeric',
    meta: { width: 260 },
  }),
  helper.accessor('bytes', {
    header: 'Tamanho do arquivo',
    sortFn: 'basic',
    meta: { width: 110, label: 'Tamanho' },
    cell: ({ row }) =>
      row.original.kind === 'folder'
        ? `${row.original.items} ${row.original.items === 1 ? 'item' : 'itens'}`
        : formatBytes(row.original.bytes),
  }),
  helper.accessor('shared', {
    header: 'Compartilhamento',
    sortFn: 'basic',
    meta: { width: 110 },
    cell: ({ getValue }) =>
      getValue() ? (
        <Link href="#compartilhamento" variant="subtle" className="max-w-full gap-1 text-inherit">
          <People16Regular className="size-4 shrink-0" />
          <span className="truncate">Compartilhado</span>
        </Link>
      ) : (
        'Particular'
      ),
  }),
  // "Atividade" é só um rótulo: nem ordena, nem se esconde.
  helper.display({
    id: 'activity',
    header: 'Atividade',
    enableSorting: false,
    enableHiding: false,
    cell: () => null,
  }),
]);

const LABELS: DataTableLabels = {
  sortAscending: 'Ascendente',
  sortDescending: 'Decrescente',
  columnSettings: 'Configurações da coluna',
  hideColumn: 'Ocultar esta coluna',
  showColumns: 'Colunas',
  selectAll: 'Selecionar todas as linhas',
  selectRow: 'Selecionar linha',
};

/* ------------------------------------------------------------------ peças */

/** Botão de ação da linha: 28px, raio 4, ícone de 16 em `--fg-tertiary`. */
function RowAction({ icon: Icon, label }: { icon: typeof Share16Regular; label: string }) {
  return (
    <Button
      variant="ghost"
      aria-label={label}
      className="text-fg-tertiary size-7 rounded-[4px] p-0"
    >
      <Icon className="size-4" />
    </Button>
  );
}

/** O menu de contexto de um item: três blocos separados por traço. */
function ItemContextMenu({ item }: { item: Item }) {
  return (
    <>
      <ContextMenuItem>
        <Share16Regular />
        Compartilhar
      </ContextMenuItem>
      <ContextMenuItem>
        <Link16Regular />
        Copiar link
      </ContextMenuItem>
      <ContextMenuItem>
        <PeopleTeam16Regular />
        Gerenciar acesso
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem>
        <Delete16Regular />
        Excluir
      </ContextMenuItem>
      <ContextMenuItem>
        <Star16Regular />
        Adicionar aos favoritos
      </ContextMenuItem>
      <ContextMenuItem>
        <ArrowDownload16Regular />
        Baixar
      </ContextMenuItem>
      <ContextMenuItem>
        <Rename16Regular />
        Renomear
      </ContextMenuItem>
      <ContextMenuItem>
        <FolderArrowRight16Regular />
        Mover para
      </ContextMenuItem>
      <ContextMenuItem>
        <DocumentCopy16Regular />
        Copiar para
      </ContextMenuItem>
      <ContextMenuItem>
        <Copy16Regular />
        Duplicata
      </ContextMenuItem>
      {item.kind === 'folder' && (
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            <Color16Regular />
            Cor da pasta
            <span aria-hidden className="ml-auto size-3 rounded-full bg-[#ffd25c]" />
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {['Amarelo', 'Vermelho', 'Verde', 'Azul', 'Roxo'].map((color) => (
              <ContextMenuItem key={color}>{color}</ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}
      <ContextMenuSeparator />
      <ContextMenuSub>
        <ContextMenuSubTrigger>
          <Flow16Regular />
          Automatizar
        </ContextMenuSubTrigger>
        <ContextMenuSubContent>
          <ContextMenuItem>Criar um fluxo</ContextMenuItem>
          <ContextMenuItem>Ver seus fluxos</ContextMenuItem>
        </ContextMenuSubContent>
      </ContextMenuSub>
      <ContextMenuSeparator />
      <ContextMenuItem>
        <Info16Regular />
        Detalhes
      </ContextMenuItem>
    </>
  );
}

/**
 * O painel "Detalhes": 342 de largura, raio 16, sem sombra (é a moldura que o separa). O
 * cabeçalho de 48 tem o ícone e o nome do item; o corpo é `--surface-subtle` com as seções
 * em cards brancos de raio 4 e 16 de padding. Sem seleção, mostra a pasta atual.
 */
function DetailsPane({ item, onClose }: { item: Item | null; onClose: () => void }) {
  return (
    <aside
      aria-label="Detalhes"
      className="border-stroke-surface bg-surface mt-[2px] flex min-h-0 w-[342px] shrink-0 flex-col overflow-hidden rounded-[16px] border"
    >
      <div className="flex h-12 shrink-0 items-center gap-2 pr-2 pl-4">
        {item ? (
          item.kind === 'folder' ? (
            <FolderIcon shared={item.shared} />
          ) : (
            <FileIcon app={item.app ?? 'word'} />
          )
        ) : (
          <FolderIcon />
        )}
        <span className="type-body1 text-fg-secondary min-w-0 flex-1 truncate">
          {item?.name ?? 'Meus arquivos'}
        </span>
        <Button
          variant="ghost"
          aria-label="Fechar o painel de detalhes"
          onClick={onClose}
          className="text-foreground size-8 rounded-[4px] p-0"
        >
          <Dismiss20Regular className="size-5" />
        </Button>
      </div>

      <div className="bg-surface-subtle flex flex-1 flex-col gap-2 overflow-y-auto p-2">
        <section className="bg-surface rounded-[4px] p-4">
          <h2 className="type-body1-strong text-fg-tertiary">Atividade</h2>
          <p className="bg-surface-subtle type-caption1 text-fg-tertiary mt-3 rounded-[4px] px-4 py-3">
            Não há nenhuma atividade recente para este item.
          </p>
        </section>

        {item && (
          <section className="bg-surface rounded-[4px] p-4">
            <dl className="flex flex-col gap-3">
              {(
                [
                  ['Tipo', itemType(item)],
                  ['Modificado', dateTime.format(item.modifiedAt)],
                  ['Data de criação', dateTime.format(item.createdAt)],
                  ['Caminho', `Meus arquivos › ${item.name}`],
                  [
                    'Tamanho',
                    item.kind === 'folder' ? `${item.bytes} bytes` : formatBytes(item.bytes),
                  ],
                ] as const
              ).map(([label, value]) => (
                <div key={label}>
                  <dt className="type-body1-strong text-fg-secondary">{label}</dt>
                  <dd className="type-body1 text-foreground truncate">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------------ a página */

function MyFilesPage() {
  const [apps, setApps] = useState<App[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const table = useDataTable<Item>({
    data: ITEMS,
    columns,
    getRowId: (item) => item.id,
    isRowRangeSelectionEvent: (event) => (event as { shiftKey?: boolean }).shiftKey === true,
    initialState: { sorting: [{ id: 'name', desc: false }] },
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  const focused = selectedRows[0]?.original ?? null;

  const toggleApp = (app: App, pressed: boolean) => {
    const next = pressed ? [...apps, app] : apps.filter((candidate) => candidate !== app);
    setApps(next);
    table.getColumn('name')?.setFilterValue(next.length > 0 ? next : undefined);
  };

  const detailsButton = (
    <DataTableCommand
      variant="outline"
      aria-pressed={detailsOpen}
      onClick={() => setDetailsOpen((open) => !open)}
      className={cn('font-normal', detailsOpen && 'bg-accent-selected')}
    >
      <PanelRight16Regular />
      Detalhes
    </DataTableCommand>
  );

  return (
    <Page scroll="content" className="gap-[21px] pr-10 pl-4 pb-[6px]">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {/* ---------------------------------------------- título: "Meus arquivos ⌄" abre o menu de criação */}
        <div className="mt-[22px]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="data-[state=open]:bg-accent-selected h-9 gap-2 rounded-lg pr-2 pl-[6px]"
              >
                <h1 className="type-title3">Meus arquivos</h1>
                <span className="text-foreground flex size-4 items-center justify-center">
                  <ChevronDown12Regular className="size-3" />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem>
                <FolderAdd16Regular />
                Nova pasta
              </DropdownMenuItem>
              <DropdownMenuItem>
                <ArrowUpload16Regular />
                Carregamento de arquivos
              </DropdownMenuItem>
              <DropdownMenuItem>
                <FolderArrowUp16Regular />
                Carregamento de pasta
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* ---------------------------------------------- os filtros (a seleção ganha a barra flutuante) */}
        <div className="mt-[27px] flex h-9 items-center gap-1">
          <Button
            variant="outline"
            aria-label="Mais filtros"
            className="border-stroke-card h-9 w-[42px] rounded-full bg-transparent p-0"
          >
            <Filter20Regular className="size-5" />
          </Button>
          {FILTER_APPS.map((app) => (
            <Toggle
              key={app}
              aria-label={`Filtrar por ${APP[app].label}`}
              pressed={apps.includes(app)}
              onPressedChange={(pressed) => toggleApp(app, pressed)}
              className="bg-surface-muted hover:bg-accent-selected data-[state=on]:bg-brand-tint h-9 w-[42px] rounded-full border border-transparent p-0"
            >
              <AppGlyph app={app} size={20} />
            </Toggle>
          ))}
          <div className="ml-auto flex items-center gap-[6px]">
            <DataTableCommand iconOnly variant="outline" aria-label="Opções de exibição">
              <AppsListDetail20Regular />
            </DataTableCommand>
            {detailsButton}
          </div>
        </div>
        <DataTableFloatingCommandBar
          open={selectedCount > 0}
          aria-label="Comandos para a seleção"
          selection={
            <DataTableSelectionCount onClick={() => table.resetRowSelection(true)}>
              {selectedCount} {selectedCount === 1 ? 'selecionado' : 'selecionados'}
            </DataTableSelectionCount>
          }
        >
          <DataTableCommand>
            <Share16Regular />
            Compartilhar
          </DataTableCommand>
          <DataTableCommand>
            <Link16Regular />
            Copiar link
          </DataTableCommand>
          <DataTableCommand>
            <Delete16Regular />
            Excluir
          </DataTableCommand>
          <DataTableCommand>
            <Star16Regular />
            Adicionar aos favoritos
          </DataTableCommand>
          <DataTableCommand>
            <ArrowDownload16Regular />
            Baixar
          </DataTableCommand>
          <DataTableCommand>
            <Copy16Regular />
            Duplicata
          </DataTableCommand>
          <DataTableCommand>
            <FolderArrowRight16Regular />
            Mover para
          </DataTableCommand>
          <DataTableCommand>
            <DocumentCopy16Regular />
            Copiar para
          </DataTableCommand>
          <DataTableCommand>
            <Rename16Regular />
            Renomear
          </DataTableCommand>
          <DataTableCommand iconOnly aria-label="Mais comandos">
            <MoreHorizontal16Regular />
          </DataTableCommand>
        </DataTableFloatingCommandBar>

        {/* ---------------------------------------------- a lista */}
        <DataTable
          table={table}
          labels={LABELS}
          aria-label="Meus arquivos"
          className="mt-4 min-h-0 flex-1"
          rowActions={() => (
            <>
              <RowAction icon={MoreHorizontal16Regular} label="Mostrar mais ações para este item" />
              <RowAction icon={Share16Regular} label="Compartilhar este item" />
              <RowAction icon={Star16Regular} label="Adicionar aos favoritos" />
            </>
          )}
          contextMenu={(row) => <ItemContextMenu item={row.original} />}
          emptyState={
            <Empty className="border-0 py-16">
              <EmptyHeader>
                <EmptyTitle className="type-subtitle2">Nenhum arquivo deste tipo</EmptyTitle>
                <EmptyDescription className="type-body1 text-fg-tertiary">
                  Desligue os filtros para ver tudo o que está em Meus arquivos.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          }
        />

        <p className="type-caption1 text-fg-tertiary mt-3 flex items-center gap-1 pl-2">
          <ChevronRight12Regular className="size-3" />
          Clique seleciona a linha; com algo marcado, soma ou tira; Shift estende; Esc limpa; botão
          direito abre o menu.
        </p>
      </div>

      {detailsOpen && <DetailsPane item={focused} onClose={() => setDetailsOpen(false)} />}
    </Page>
  );
}
